import React, { useEffect, useState } from 'react';
import '../BuyerSelectionPopup.scss';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { EngagementData } from '../../../../slices/marketplace/timeline/models/engagement-data.model';
import { EngagementProcess } from '../../../../slices/marketplace/timeline/models/engagement-process.model';
import BootstrapTable from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { EngagementBuyer } from '../../../../slices/marketplace/timeline/models/engagement-buyer.model';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { MarketplaceOpportunityStatus } from '../../../../slices/marketplace/opportunity/models/opportunity-status';
import { useStateSelector } from '../../../../store/selectors';
import { LevelOfService, isSfas } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import Helpers from '../../../../utils/helper';
import { TimelineActionStatus } from '../../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import {
    DocumentStatusExecutionStatus,
    DocumentStatusExecutionStatusOptions,
} from '../../../../slices/marketplace/timeline/models/enums/document-execution-status.enum';
import './IdNdaSelectionForm.scss';
import { isSelectableBuyer } from '../../../../utils/isSelectableBuyer';
import { TimelineEventStatus } from '../../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { EngagementProcessStatus } from '../../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import { PricesCalculationHelper } from '../../../../utils/pricesCalculationHelper';
import { buyersAlreadySent, SelectionRound } from '../../../../slices/marketplace/timeline/utils/catch-up-wait';
import { useBuyerSelectionPaidBefore } from '../../../../custom-hooks/useBuyerSelectionPaidBefore';

// A buyer the seller has already paid-and-sent the ID NDA to reads either Engaged (sent and signed)
// or Awaiting Buyer Response (sent, not yet signed — #1733). Both count as "already sent" so the seller
// isn't offered a Send checkbox for them again and charged a second time.
const ID_NDA_ALREADY_SENT_STATUSES = [
    MarketplaceOpportunityStatus.Engaged,
    MarketplaceOpportunityStatus.AwaitingBuyerResponse,
];

interface IdNdaSelectionFormProps {
    isAdminView: boolean;
    isReadOnly?: boolean;
    targetEvent: TimelineEvent;
    engagementData: EngagementData;
    engagementProcess: EngagementProcess;
    engagementId: number;
    setSelectedBuyerId(value: number): void;
    setIsBuyerProfilePopupVisible(value: boolean): void;
    isLoading: boolean;
    onPayAndSendClick(
        selectedBuyerIds: number[],
        pricePerBuyer: number,
        totalPrice: number,
        firstNAsOneCount: number,
        eventId: string,
        creditablePortion: number,
        chargedBuyersCount: number
    ): void;
    onCancelClick(): void;
    onFinalizeSelectionClick(): void;
    setTypeOfSelectionForConfirmation(): void;
    eventId: string;
    actionId: number;
    sellersOwnRound?: SelectionRound;
    levelOfService: LevelOfService;
}

export const IdNdaSelectionForm = (props: IdNdaSelectionFormProps) => {
    const [selectedBuyerIds, setSelectedBuyerIds] = useState<number[]>([]);

    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const [alreadySelectedBuyerIds, setAlreadySelectedBuyerIds] = useState<number[]>([]);
    const paidBeforeBuyerIds = useBuyerSelectionPaidBefore(
        props.engagementId,
        props.eventId,
        !isSfas(props.levelOfService)
    );
    const [chargedBuyersCount, setChargedBuyersCount] = useState(0);
    const [pricePerBuyer, setPricePerBuyer] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [firstNAsOneCount, setFirstNAsOneCount] = useState(0);
    const [creditablePortion, setCreditablePortion] = useState(0);

    useEffect(() => {
        setAlreadySelectedBuyerIds(
            buyersAlreadySent(
                [{ process: props.engagementProcess, actionId: props.actionId }, props.sellersOwnRound],
                ID_NDA_ALREADY_SENT_STATUSES
            )
        );
    }, []);

    useEffect(() => {
        calculateTotalPrice();
    }, [selectedBuyerIds, alreadySelectedBuyerIds, paidBeforeBuyerIds]);

    const columns = [
        {
            dataField: 'companyName',
            text: 'Buyer',
            sort: false,
            formatter: (cellContent: string, currentBuyer: EngagementBuyer) => (
                <div
                    className="link-text"
                    onClick={() => {
                        props.setSelectedBuyerId(currentBuyer.buyerId);
                        props.setIsBuyerProfilePopupVisible(true);
                    }}>
                    {cellContent}
                </div>
            ),
        },
        {
            dataField: 'id',
            text: 'Status',
            sort: false,
            formatter: (
                _cellContent: number,
                currentBuyer: EngagementBuyer
            ) => {
                const buyerStates = currentBuyer.actionState;
                const currentActionState = buyerStates.find(
                    (x) => x.actionId === props.actionId
                );
                // #932: resolved by the same rules as the Buyers table's "Buyer Execution of
                // ID NDA" column — no action, a failed send, or a seller-rejected buyer all
                // read "Not Offered" (rejection overrides even an executed document).
                if (
                    !currentActionState ||
                    currentActionState.status === TimelineActionStatus.Failed ||
                    currentActionState.buyerStatus ===
                        MarketplaceOpportunityStatus.RejectedBySeller
                ) {
                    return <span>Not Offered</span>;
                }
                if (
                    currentActionState.status !== TimelineActionStatus.Completed
                ) {
                    return (
                        <span>
                            {
                                DocumentStatusExecutionStatusOptions[
                                    DocumentStatusExecutionStatus.Offered
                                ]
                            }
                        </span>
                    );
                }

                return (
                    <span>
                        {
                            DocumentStatusExecutionStatusOptions[
                                DocumentStatusExecutionStatus.Executed
                            ]
                        }
                    </span>
                );
            },
        },
        ...(!props.isAdminView
            ? [
                  {
                      dataField: 'buyerId',
                      text: 'Send',
                      sort: false,
                      formatter: (
                          cellContent: number,
                          currentBuyer: EngagementBuyer
                      ) => {
                          const buyerStates = currentBuyer.actionState;
                          const currentActionState = buyerStates.find(
                              (x) => x.actionId === props.actionId
                          );
                          const isBuyerSelected =
                              currentActionState != null &&
                              ID_NDA_ALREADY_SENT_STATUSES.includes(
                                  currentActionState.buyerStatus
                              );
                          return isBuyerSelected === true ? (
                              <></>
                          ) : (
                              <input
                                  type="checkbox"
                                  checked={selectedBuyerIds.includes(
                                      cellContent
                                  )}
                                  disabled={
                                      props.isReadOnly ||
                                      isSelectionFinalized ||
                                      props.targetEvent?.status ===
                                          TimelineEventStatus.Completed ||
                                      props.engagementProcess?.status ===
                                          EngagementProcessStatus.Completed
                                  }
                                  onChange={(event) => {
                                      const isChecked = event.target.checked;
                                      if (isChecked === true) {
                                          setSelectedBuyerIds([
                                              ...selectedBuyerIds,
                                              cellContent,
                                          ]);
                                      } else {
                                          setSelectedBuyerIds(
                                              selectedBuyerIds.filter(
                                                  (id) => id !== cellContent
                                              )
                                          );
                                      }
                                  }}
                              />
                          );
                      },
                  },
              ]
            : []),
    ];

    const calculateTotalPrice = () => {
        const levelOfService = props.levelOfService;

        const brokreagePricing =
            PricesCalculationHelper.getBrokeragePricingByLevelOfService(
                levelOfService,
                marketplacePricing
            );

        const scheduleRow = PricesCalculationHelper.getScheduleRow(brokreagePricing.transactionFeeSchedule, props.engagementProcess.startLtmRevenue);
        const pricePerBuyer = scheduleRow?.idNdaFeePerBuyer ?? 0;
        const creditablePortion =
            brokreagePricing.transactionFees.idNdaFeeCreditable;
        const idNdaFirstXCount =
            brokreagePricing.transactionFees.idNdaFirstXCount;

        // [#1309] Buyers paid for on this step before are selected again at no charge.
        const { chargedBuyersCount, totalPrice: orderTotalPrice } =
            PricesCalculationHelper.priceBuyerSelection(
                selectedBuyerIds,
                alreadySelectedBuyerIds,
                paidBeforeBuyerIds,
                idNdaFirstXCount,
                pricePerBuyer
            );
        const creditableValue = (creditablePortion * orderTotalPrice) / 100;
        setPricePerBuyer(pricePerBuyer);
        setChargedBuyersCount(chargedBuyersCount);
        setTotalPrice(orderTotalPrice);
        setFirstNAsOneCount(idNdaFirstXCount);
        setCreditablePortion(creditableValue);
    };

    const isSelectionFinalized =
        props.targetEvent?.linkedAction?.isSelectionFinalized === true;

    return (
        <div className="id-nda-selection-form">
            {isSelectionFinalized && (
                <div className="selection-finalized-banner">
                    Buyer selection is locked. Selected buyers can still sign
                    the NDA until the event deadline.
                </div>
            )}
            <BootstrapTable
                striped
                key={selectedBuyerIds.length}
                bordered
                bootstrap4
                keyField="Id"
                data={
                    props.engagementProcess?.buyers?.filter(
                        isSelectableBuyer
                    ) ?? []
                }
                columns={columns}
                noDataIndication="No Data available"
            />
            {!isSfas(props.levelOfService) && (
                <>
                    <div className="bold">
                        Total Cost:{' '}
                        {Helpers.GetCurrencyFormat(totalPrice, 2, true)}
                    </div>
                    <div>
                        Creditable Portion:{' '}
                        {Helpers.GetCurrencyFormat(creditablePortion, 2, true)}
                    </div>
                </>
            )}
            <div className="buttons-wrapper">
                <button
                    className="btn btn-cancel"
                    disabled={props.isLoading}
                    onClick={() => {
                        props.onCancelClick();
                    }}>
                    Cancel
                </button>
                {!props.isAdminView && (
                    <>
                        <ButtonLoader
                            buttonText={
                                isSfas(props.levelOfService)
                                    ? 'Send'
                                    : 'Pay & Send'
                            }
                            loaderButtonText={''}
                            disabled={
                                props.isReadOnly ||
                                props.isLoading ||
                                isSelectionFinalized ||
                                selectedBuyerIds.length === 0
                            }
                            isLoading={props.isLoading}
                            onClick={() => {
                                props.onPayAndSendClick(
                                    selectedBuyerIds,
                                    pricePerBuyer,
                                    totalPrice,
                                    firstNAsOneCount,
                                    props.eventId,
                                    creditablePortion,
                                    chargedBuyersCount
                                );
                            }}
                            className={'btn btn-primary'}
                        />
                        <button
                            className="btn btn-primary"
                            disabled={
                                props.isReadOnly ||
                                props.isLoading ||
                                isSelectionFinalized ||
                                props.targetEvent?.status ===
                                    TimelineEventStatus.Completed
                            }
                            onClick={() => {
                                props.setTypeOfSelectionForConfirmation();
                                props.onFinalizeSelectionClick();
                            }}>
                            Finalize selection
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
